import { logger } from "../services/loggingService";

export type BoxUploadFile = {
    data: Blob;
    name: string;
};

export const MAX_FILE_GENERATION_ATTEMPTS = 3;
export const FILE_GENERATION_RETRY_DELAY_MS = 750;

export function isValidBoxUploadFile(
    file: { data?: Blob | null; name?: string | null } | null | undefined,
): file is BoxUploadFile {
    return (
        file?.data instanceof Blob &&
        file.data.size > 0 &&
        typeof file.name === "string" &&
        file.name.trim().length > 0
    );
}

export function isGrayscaleTopviewFile(file: { name?: string | null } | null | undefined): boolean {
    return (file?.name ?? "").toLowerCase().includes("grayscale");
}

/** Box / multipart uploads often reject names with path chars, control chars, or extreme length. */
export function analyzeBoxFileName(fileName: string): {
    length: number;
    extension: string | null;
    hasPathSeparators: boolean;
    hasControlChars: boolean;
    hasSuspiciousChars: boolean;
    warnings: string[];
} {
    const warnings: string[] = [];
    const extensionMatch = fileName.match(/\.([^.]+)$/);
    const extension = extensionMatch?.[1]?.toLowerCase() ?? null;
    const hasPathSeparators = /[\\/]/.test(fileName);
    const hasControlChars = /[\x00-\x1f\x7f]/.test(fileName);
    // Characters that frequently break Box or middleware filename handling
    const hasSuspiciousChars = /[<>:"|?*#%]/.test(fileName);

    if (!fileName?.trim()) warnings.push("missing_file_name");
    if (fileName.length > 255) warnings.push("name_too_long");
    if (hasPathSeparators) warnings.push("path_separators");
    if (hasControlChars) warnings.push("control_chars");
    if (hasSuspiciousChars) warnings.push("suspicious_chars");
    if (!extension) warnings.push("missing_extension");
    if (fileName !== fileName.trim()) warnings.push("leading_or_trailing_whitespace");

    return {
        length: fileName.length,
        extension,
        hasPathSeparators,
        hasControlChars,
        hasSuspiciousChars,
        warnings,
    };
}

export async function boxFileFromImageUrl(
    url: string | null | undefined,
    name: string,
    label: string,
): Promise<BoxUploadFile> {
    if (!url) {
        throw new Error(`${label} image is missing`);
    }

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch ${label} image (${response.status})`);
    }

    const data = await response.blob();
    if (!(data instanceof Blob) || data.size === 0) {
        throw new Error(`${label} image is empty`);
    }

    return { data, name };
}

type BoxUploadFileDraft = { data?: Blob | null; name?: string | null } | null;

export async function generateBoxFilesWithRetry(
    reportLabel: string,
    generate: () => Promise<BoxUploadFileDraft[]>,
): Promise<(BoxUploadFile | null)[]> {
    let lastError: unknown;
    let lastFiles: BoxUploadFileDraft[] = [];

    for (let attempt = 1; attempt <= MAX_FILE_GENERATION_ATTEMPTS; attempt++) {
        try {
            lastFiles = (await generate()) ?? [];
            if (lastFiles.length > 0 && lastFiles.every(isValidBoxUploadFile)) {
                if (attempt > 1) {
                    logger.info(`Box upload file '${reportLabel}' succeeded on attempt ${attempt}`);
                }
                return lastFiles;
            }
            lastError = new Error(
                `Invalid blob (attempt ${attempt}/${MAX_FILE_GENERATION_ATTEMPTS})`,
            );
            logger.warn(`Box upload file '${reportLabel}' empty/null on attempt ${attempt}`, {
                attempt,
                reportLabel,
                files: lastFiles.map((file) => ({
                    name: file?.name ?? null,
                    blobSize: file?.data?.size ?? 0,
                })),
            });
        } catch (error) {
            lastError = error;
            logger.warn(`Box upload file '${reportLabel}' threw on attempt ${attempt}`, error);
        }

        if (attempt < MAX_FILE_GENERATION_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, FILE_GENERATION_RETRY_DELAY_MS));
        }
    }

    logger.error(
        `Box upload file '${reportLabel}' failed after ${MAX_FILE_GENERATION_ATTEMPTS} attempts`,
        lastError,
    );
    return lastFiles.map((file) => (isValidBoxUploadFile(file) ? file : null));
}

export async function generateBoxFileWithRetry(
    reportLabel: string,
    generate: () => Promise<BoxUploadFileDraft>,
): Promise<BoxUploadFile | null> {
    const [file] = await generateBoxFilesWithRetry(reportLabel, async () => [await generate()]);
    return file ?? null;
}
